import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Banner } from '@liminal/web-ui/brand/generated/Banner';
import { styled } from '@mui/material/styles';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import type { IconName } from '@liminal/web-ui/icons/registry';
import { fontSizes, fontWeights, letterSpacing } from '@liminal/brand/scale';

const DRAWER_WIDTH = 260;

const SidebarRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const LogoArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3, 3),
}));

const NavList = styled(List)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1, 1.5),
}));

const NavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(0.5),
  // A selected or hovered row is a surface, not the page background. This used
  // to read `palette.background.default`, which was cream only because the old
  // admin-local theme defined it that way; the shared palette follows mobile,
  // where background.primary is white. Pointing at surface.secondary keeps the
  // cream and says what it means.
  ...(active
    ? {
        backgroundColor: theme.palette.surface.secondary,
        color: theme.palette.primary.main,
        borderLeft: '3px solid',
        borderLeftColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.surface.secondary,
        },
      }
    : {
        color: theme.palette.text.disabled,
        '&:hover': {
          backgroundColor: theme.palette.surface.secondary,
          color: theme.palette.text.primary,
        },
      }),
}));

const NavIcon = styled(ListItemIcon)({
  minWidth: 40,
  color: 'inherit',
});

// The subtitle over each group, set like the command palette's category label.
const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: fontSizes.xs,
  fontWeight: fontWeights.semibold,
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.wide}px`,
  color: theme.palette.text.disabled,
  padding: theme.spacing(2, 2, 0.5),
}));

const UserInfoArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
}));

const LogoutButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  padding: 0,
  minWidth: 'auto',
  fontSize: fontSizes.sm,
  color: theme.palette.text.disabled,
  textTransform: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const PermanentDrawer = styled(Drawer)({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
  },
});

const TemporaryDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
  },
});

// `icon` is registry data, not a component — typing it as IconName means a
// glyph that is not in the shared registry is a compile error rather than an
// Icon throwing at runtime.
interface NavItemData {
  name: string;
  href: string;
  icon: IconName;
}

const navSections: readonly { title: string; items: readonly NavItemData[] }[] = [
  {
    title: 'Customers',
    items: [
      { name: 'Users', href: '/users', icon: 'people' },
      { name: 'Accounts', href: '/accounts', icon: 'wallet' },
      { name: 'Transactions', href: '/transactions', icon: 'swap' },
    ],
  },
  {
    title: 'Nim',
    items: [
      { name: 'Nim', href: '/nim', icon: 'sparkles' },
      { name: 'Conversations', href: '/conversations', icon: 'chat' },
      // Flagged Nim replies. Not the flag glyph: that is Flags (feature flags), below.
      { name: 'Flagged', href: '/flagged', icon: 'thumbs-down' },
      { name: 'Feedback', href: '/feedback', icon: 'bug' },
    ],
  },
  {
    title: 'Growth',
    items: [
      { name: 'Announcements', href: '/announcements', icon: 'megaphone' },
      { name: 'Nudges', href: '/nudges', icon: 'notifications' },
      // How each referral was attributed to its inviter (LIM-4840).
      { name: 'Referrals', href: '/referrals', icon: 'stats-chart' },
      { name: 'Rewards', href: '/rewards', icon: 'gift' },
      // The current event's door (LIM-4707): /events opens its check-in, and the
      // item stays lit on any event's pages.
      { name: 'Check-in', href: '/events', icon: 'calendar' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { name: 'Flags', href: '/fflags', icon: 'flag' },
      { name: 'System', href: '/system', icon: 'settings' },
    ],
  },
];

interface SidebarProps {
  userEmail: string;
  onLogout: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  currentPath: string;
}

interface SidebarContentProps extends Omit<SidebarProps, 'open' | 'setOpen'> {
  /**
   * The phone drawer's close. It puts a close button beside the logo, and
   * picking a page closes the drawer too, so the page shows.
   */
  onClose?: () => void;
}

function SidebarContent({ userEmail, onLogout, currentPath, onClose }: SidebarContentProps) {
  const navigate = useNavigate();

  return (
    <SidebarRoot>
      {/* Logo. The full banner lockup carries the accessible name. */}
      <LogoArea>
        <Banner size={40} title="Liminal" />
        {onClose ? <IconButton icon="close" label="Close menu" size="lg" onClick={onClose} /> : null}
      </LogoArea>

      {/* Navigation */}
      <NavList>
        {navSections.map((section) => (
          <Box key={section.title}>
            <SectionLabel>{section.title}</SectionLabel>
            {section.items.map((item) => {
              const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/');

              return (
                <NavItem
                  key={item.href}
                  active={isActive}
                  onClick={() => {
                    navigate(item.href);
                    onClose?.();
                  }}
                >
                  <NavIcon>
                    <Icon name={item.icon} size={20} />
                  </NavIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: fontSizes.md,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                </NavItem>
              );
            })}
          </Box>
        ))}
      </NavList>

      {/* User info & logout */}
      <Divider />
      <UserInfoArea>
        <Typography variant="body2" noWrap color="text.primary">
          {userEmail}
        </Typography>
        <LogoutButton onClick={onLogout} variant="text" color="inherit">
          Log out
        </LogoutButton>
      </UserInfoArea>
    </SidebarRoot>
  );
}

export function Sidebar({ userEmail, onLogout, open, setOpen, currentPath }: SidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (isDesktop) {
    return (
      <PermanentDrawer variant="permanent">
        <SidebarContent userEmail={userEmail} onLogout={onLogout} currentPath={currentPath} />
      </PermanentDrawer>
    );
  }

  const close = () => setOpen(false);

  return (
    <TemporaryDrawer
      variant="temporary"
      open={open}
      onClose={close}
      ModalProps={{ keepMounted: true }}
    >
      <SidebarContent userEmail={userEmail} onLogout={onLogout} currentPath={currentPath} onClose={close} />
    </TemporaryDrawer>
  );
}

export { DRAWER_WIDTH };
